/**
 * grantFormHelpers — shared form-state shape + per-engine builders for the
 * GrantEditFormBody (Slice F2 — GRANT / REVOKE UI).
 *
 * Privileges are kept as an opaque uppercase keyword list (matches DSE2's
 * `GrantDef.privileges` posture). The form lets the user pick from a curated
 * common set per object kind plus a free-form input — engines accept far
 * more privileges than we surface, so power users drop into Edit-as-SQL.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  GrantDef,
  GrantObject,
  GrantObjectKind,
  GrantPrincipal,
  GrantPrincipalKind,
  RevokeDef,
} from '../../../../sql-resolver/types/ddlEdit';
import type { ParsedParkedGrant } from '../../../../sql-resolver/ddl/extract/grant';

export type GrantEditMode = 'grant' | 'revoke';

export interface GrantFormGrantee {
  readonly kind: GrantPrincipalKind;
  readonly name: string;
  readonly mysqlHost: string;
}

export interface GrantFormValues {
  readonly mode: GrantEditMode;
  /** Uppercase privilege keywords. */
  readonly privileges: readonly string[];
  readonly objectKind: GrantObjectKind;
  readonly objectSchema: string;
  readonly objectName: string;
  readonly grantees: readonly GrantFormGrantee[];
  readonly withGrantOption: boolean;
  /** Oracle-only WITH ADMIN OPTION (mutually exclusive with grant option). */
  readonly oracleWithAdminOption: boolean;
  /** REVOKE-only: emit CASCADE (mssql / postgres). */
  readonly cascade: boolean;
  /** Postgres-only REVOKE: emit GRANT OPTION FOR. */
  readonly pgsqlGrantOptionFor: boolean;
  /** Postgres-only: ALL PRIVILEGES shortcut. */
  readonly pgsqlAllPrivileges: boolean;
}

export const DEFAULT_GRANT_FORM_VALUES: GrantFormValues = {
  mode: 'grant',
  privileges: ['SELECT'],
  objectKind: 'table',
  objectSchema: '',
  objectName: '',
  grantees: [{ kind: 'user', name: '', mysqlHost: '' }],
  withGrantOption: false,
  oracleWithAdminOption: false,
  cascade: false,
  pgsqlGrantOptionFor: false,
  pgsqlAllPrivileges: false,
};

const COMMON_TABLE_PRIVS = ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'REFERENCES', 'TRIGGER'] as const;
const COMMON_VIEW_PRIVS = ['SELECT', 'INSERT', 'UPDATE', 'DELETE'] as const;
const COMMON_SEQUENCE_PRIVS = ['SELECT', 'USAGE', 'UPDATE'] as const;
const COMMON_FUNCTION_PRIVS = ['EXECUTE'] as const;
const COMMON_SCHEMA_PRIVS = ['USAGE', 'CREATE'] as const;
const COMMON_DATABASE_PRIVS = ['CREATE', 'CONNECT', 'TEMPORARY'] as const;
const COMMON_TYPE_PRIVS = ['USAGE'] as const;
const COMMON_TABLESPACE_PRIVS = ['CREATE'] as const;
const ORACLE_SYSTEM_PRIVS = [
  'CREATE SESSION', 'CREATE TABLE', 'CREATE VIEW', 'CREATE PROCEDURE',
  'SELECT ANY TABLE', 'INSERT ANY TABLE', 'UPDATE ANY TABLE', 'DELETE ANY TABLE',
  'CREATE TABLESPACE', 'DBA',
] as const;

export function getCommonPrivilegesForKind(kind: GrantObjectKind): readonly string[] {
  switch (kind) {
    case 'table': return COMMON_TABLE_PRIVS;
    case 'view': return COMMON_VIEW_PRIVS;
    case 'sequence': return COMMON_SEQUENCE_PRIVS;
    case 'function': case 'procedure': case 'routine': case 'package':
      return COMMON_FUNCTION_PRIVS;
    case 'schema': return COMMON_SCHEMA_PRIVS;
    case 'database': return COMMON_DATABASE_PRIVS;
    case 'type': return COMMON_TYPE_PRIVS;
    case 'tablespace': return COMMON_TABLESPACE_PRIVS;
    case 'system': return ORACLE_SYSTEM_PRIVS;
    case 'role': return [];
  }
}

export function getSupportedObjectKindsForEngine(engine: SqlEngine): readonly GrantObjectKind[] {
  if (engine === 'sqlite') return [];
  if (engine === 'mssql') {
    return ['table', 'view', 'schema', 'database', 'function', 'procedure', 'type', 'role'];
  }
  if (engine === 'postgres') {
    return ['table', 'view', 'sequence', 'schema', 'database', 'function',
      'procedure', 'type', 'tablespace', 'role'];
  }
  if (engine === 'mysql') {
    return ['table', 'database', 'function', 'procedure', 'role'];
  }
  // oracle
  return ['table', 'view', 'sequence', 'function', 'procedure', 'package',
    'type', 'role', 'system'];
}

export function buildGrantObject(values: GrantFormValues): GrantObject {
  // Role-membership grants carry NO object name: the role's identity lives in
  // the privilege list (`GRANT <role> TO <principal>`) and the emitter's
  // `formatObjectClause` returns '' for `kind:'role'`. Leaking the role name
  // into `object.name` would silently drop it (the emitter never reads it),
  // so the bare `{ kind: 'role' }` is the correct shape — see
  // dialect.grant.ts / applyDdlEdit.grant.test.ts "ROLE membership".
  if (values.objectKind === 'role') {
    return { kind: 'role' };
  }
  return {
    kind: values.objectKind,
    ...(values.objectName.trim() ? { name: values.objectName.trim() } : {}),
    ...(values.objectSchema.trim() ? { schema: values.objectSchema.trim() } : {}),
  };
}

/** Role-membership privilege list — for a role grant the role NAME *is* the
 *  privilege (`GRANT data_reader TO app_user`). The role-name input writes to
 *  `objectName` (the field is relabelled "Role name" in GrantFormFields), so
 *  we lift it back into the privilege slot here. Returns `[]` for an empty /
 *  whitespace-only role name so validation rejects it rather than the form
 *  silently emitting the stale default `['SELECT']`. */
function rolePrivileges(values: GrantFormValues): readonly string[] {
  const roleName = values.objectName.trim();
  return roleName.length > 0 ? [roleName] : [];
}

export function buildGrantees(
  engine: SqlEngine,
  values: GrantFormValues,
): readonly GrantPrincipal[] {
  return values.grantees
    .filter((g) => g.kind === 'public' || g.name.trim().length > 0)
    .map((g): GrantPrincipal => {
      if (g.kind === 'public') return { kind: 'public' };
      return {
        kind: g.kind,
        name: g.name.trim(),
        ...(engine === 'mysql' && g.mysqlHost.trim()
          ? { mysqlHost: g.mysqlHost.trim() }
          : {}),
      };
    });
}

export function buildGrantDef(engine: SqlEngine, values: GrantFormValues): GrantDef {
  // Role-membership: the role name (typed into the "Role name" field, stored
  // in `objectName`) IS the privilege. Preserve its case verbatim — role
  // identifiers are case-sensitive on several engines — and never fall back to
  // the form's default `['SELECT']`, which would emit a stale SELECT grant and
  // drop the role entirely (bug F23).
  const privileges = values.objectKind === 'role'
    ? rolePrivileges(values)
    : values.pgsqlAllPrivileges && engine === 'postgres'
      ? []
      : values.privileges.map((p) => p.trim().toUpperCase()).filter((p) => p.length > 0);
  return {
    privileges,
    object: buildGrantObject(values),
    grantees: buildGrantees(engine, values),
    ...(values.withGrantOption && !(engine === 'oracle' && values.oracleWithAdminOption)
      ? { withGrantOption: true }
      : {}),
    ...(engine === 'oracle' && values.oracleWithAdminOption
      ? { oracleWithAdminOption: true }
      : {}),
    ...(engine === 'postgres' && values.pgsqlAllPrivileges
      ? { pgsqlAllPrivileges: true }
      : {}),
  };
}

export function buildRevokeDef(engine: SqlEngine, values: GrantFormValues): RevokeDef {
  // Mirror buildGrantDef: a role-membership REVOKE (`REVOKE <role> FROM …`)
  // carries the role name in the privilege list, not in object.name (bug F23).
  const privileges = values.objectKind === 'role'
    ? rolePrivileges(values)
    : values.pgsqlAllPrivileges && engine === 'postgres'
      ? []
      : values.privileges.map((p) => p.trim().toUpperCase()).filter((p) => p.length > 0);
  return {
    privileges,
    object: buildGrantObject(values),
    grantees: buildGrantees(engine, values),
    ...(values.cascade && (engine === 'mssql' || engine === 'postgres')
      ? { cascade: true }
      : {}),
    ...(engine === 'postgres' && values.pgsqlGrantOptionFor
      ? { pgsqlGrantOptionFor: true }
      : {}),
    ...(engine === 'postgres' && values.pgsqlAllPrivileges
      ? { pgsqlAllPrivileges: true }
      : {}),
  };
}

export function valuesFromParsedGrant(
  parsed: ParsedParkedGrant,
): Partial<GrantFormValues> {
  const grantees: GrantFormGrantee[] = parsed.grantees.map((g) => ({
    kind: g.kind,
    name: g.name,
    mysqlHost: g.mysqlHost ?? '',
  }));
  // Role-membership grants parse with NO `ON` clause, so the extractor emits
  // `object: null` and carries the role name in `privileges` (`GRANT
  // data_reader TO app_user` → privileges:['data_reader']). Route that role
  // name into the "Role name" field (objectName) and flag the form as a role
  // grant so buildGrantDef rebuilds the role name BACK into `privileges` —
  // keeping the two directions consistent (bug F23). The explicit
  // `object.kind === 'role'` form (if a future parser emits it) is handled the
  // same way, reading the name from object.name with a privilege fallback.
  const isRoleGrant = parsed.object === null || parsed.object?.kind === 'role';
  if (isRoleGrant) {
    const roleName = parsed.object?.name ?? parsed.privileges[0] ?? '';
    return {
      mode: parsed.action,
      objectKind: 'role',
      objectName: roleName,
      privileges: [],
      ...(grantees.length > 0 ? { grantees } : {}),
      ...(parsed.withGrantOption ? { withGrantOption: true } : {}),
      ...(parsed.cascade ? { cascade: true } : {}),
      ...(parsed.pgsqlGrantOptionFor ? { pgsqlGrantOptionFor: true } : {}),
    };
  }
  const out: Partial<GrantFormValues> = {
    mode: parsed.action,
    privileges: parsed.privileges,
    ...(parsed.object?.kind ? { objectKind: parsed.object.kind } : {}),
    ...(parsed.object?.name !== undefined ? { objectName: parsed.object.name } : {}),
    ...(parsed.object?.schema !== undefined ? { objectSchema: parsed.object.schema } : {}),
    ...(grantees.length > 0 ? { grantees } : {}),
    ...(parsed.withGrantOption ? { withGrantOption: true } : {}),
    ...(parsed.cascade ? { cascade: true } : {}),
    ...(parsed.pgsqlGrantOptionFor ? { pgsqlGrantOptionFor: true } : {}),
  };
  return out;
}

/** Form-level validation that runs BEFORE the engine-level
 *  `validateGrantForEngine`. Today it owns the one rule the engine validator
 *  can only express generically: a role-membership grant needs a non-empty
 *  role name (the role name lives in the "Role name" field / `objectName`, and
 *  an empty value would otherwise build `privileges: []` and surface the
 *  generic "At least one privilege is required" error). Returns a
 *  human-readable message to display, or `null` when the values pass. */
export function validateGrantFormValues(values: GrantFormValues): string | null {
  if (values.objectKind === 'role' && values.objectName.trim().length === 0) {
    return 'A role name is required for a role-membership grant.';
  }
  return null;
}

export function isGrantSupportedForEngine(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getGrantUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  return '';
}
